import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { voucherService, formatCurrency } from '../../services/voucherService';
import './VoucherSuccess.css';

const VoucherSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [voucherData, setVoucherData] = useState<{
    code: string;
    amount: number;
    message?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // T-129 (4) (v1.8.38): PayPal prüft die Zahlung noch (kein Fehler, kein "Erneut versuchen", sonst Doppelkauf)
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const amount = searchParams.get('amount');
    const voucherId = searchParams.get('voucherId');
    const token = searchParams.get('token'); // PayPal token
    const sessionId = searchParams.get('session_id'); // T-120 (v1.8.33): Stripe Checkout

    // T-120 (v1.8.33): Rückkehr von Stripe (Kreditkarte): Server prüft die Session und aktiviert den Gutschein
    if (voucherId && sessionId && searchParams.get('provider') === 'stripe') {
      confirmStripePayment(parseInt(voucherId), sessionId);
    // If we have a PayPal token, capture the order
    } else if (voucherId && token) {
      capturePayPalOrder(parseInt(voucherId), token);
    } else if (code && amount) {
      setVoucherData({
        code,
        amount: parseFloat(amount),
      });
    }
  }, [searchParams]);

  const confirmStripePayment = async (voucherId: number, sessionId: string) => {
    setIsProcessing(true);
    setError(null);
    try {
      const result = await voucherService.confirmStripePayment(voucherId, sessionId);
      if (result.success) {
        setVoucherData({ code: result.voucherCode, amount: result.amount, message: result.message });
      } else {
        setError(result.message || 'Zahlung konnte nicht abgeschlossen werden');
      }
    } catch (err: any) {
      setError(err.message || 'Ein Fehler ist aufgetreten');
    } finally {
      setIsProcessing(false);
    }
  };

  const capturePayPalOrder = async (voucherId: number, paypalToken: string) => {
    setIsProcessing(true);
    setError(null);

    try {
      const result = await voucherService.capturePayPalOrder(voucherId, paypalToken);
      if (result.success) {
        setVoucherData({
          code: result.voucherCode,
          amount: result.amount,
          message: result.message,
        });
      } else if (result.status === 'PENDING') {
        setPending(result.message || 'Ihre PayPal-Zahlung wird von PayPal noch geprüft.');
      } else {
        setError(result.message || 'Zahlung konnte nicht abgeschlossen werden');
      }
    } catch (err: any) {
      setError(err.message || 'Ein Fehler ist aufgetreten');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isProcessing) {
    return (
      <div className="voucher-success-page">
        <div className="voucher-success-container processing">
          <div className="processing-spinner"></div>
          <h2>Zahlung wird verarbeitet...</h2>
          <p>Bitte warten Sie einen Moment.</p>
        </div>
      </div>
    );
  }

  if (pending) {
    return (
      <div className="voucher-success-page">
        <div className="voucher-success-container">
          <h2>Zahlung in Prüfung</h2>
          <p>{pending}</p>
          <p>Sie müssen nichts weiter tun und bitte keinen neuen Gutschein kaufen. Bei Fragen erreichen Sie uns unter info@frankenfloss.de.</p>
          <Link to="/" className="btn-primary">
            Zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="voucher-success-page">
        <div className="voucher-success-container error">
          <div className="error-icon">!</div>
          <h2>Zahlung fehlgeschlagen</h2>
          <p>{error}</p>
          <Link to="/gutschein-kaufen" className="btn-primary">
            Erneut versuchen
          </Link>
        </div>
      </div>
    );
  }

  if (!voucherData) {
    return (
      <div className="voucher-success-page">
        <div className="voucher-success-container">
          <div className="loading-spinner" style={{ width: '48px', height: '48px', margin: '0 auto' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="voucher-success-page">
      <div className="voucher-success-container">
        <div className="success-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22,4 12,14.01 9,11.01" />
          </svg>
        </div>

        <h1>Vielen Dank für Ihren Kauf!</h1>
        <p className="success-subtitle">
          Ihr Frankenfloss-Gutschein wurde erfolgreich erstellt.
        </p>

        <div className="voucher-card">
          <div className="voucher-card-header">
            <img src="/logo-frankenfloss.svg" alt="Frankenfloss" />
            <span>GESCHENK-GUTSCHEIN</span>
          </div>
          <div className="voucher-card-body">
            <div className="voucher-code">
              <label>Ihr Gutschein-Code:</label>
              <strong>{voucherData.code}</strong>
            </div>
            <div className="voucher-amount">
              <label>Wert:</label>
              <strong>{formatCurrency(voucherData.amount)}</strong>
            </div>
          </div>
          <div className="voucher-card-footer">
            3 Jahre gültig ab Kaufdatum
          </div>
        </div>

        <div className="success-info">
          <h3>Was passiert jetzt?</h3>
          <ul>
            <li>
              <span className="step-icon">1</span>
              <span>Sie erhalten eine Bestätigungs-E-Mail mit Ihrem Gutschein-Code.</span>
            </li>
            <li>
              <span className="step-icon">2</span>
              <span>Der PDF-Gutschein zum Ausdrucken oder Weiterschicken ist der E-Mail angehängt.</span>
            </li>
            <li>
              <span className="step-icon">3</span>
              <span>Der Gutschein kann bei jeder Buchung eingelöst werden.</span>
            </li>
          </ul>
        </div>

        <div className="success-actions">
          <Link to="/gutschein-kaufen" className="btn-secondary">
            Weiteren Gutschein kaufen
          </Link>
          <Link to="/" className="btn-primary">
            Zur Startseite
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VoucherSuccess;
